import {GameModel} from "@nolimitcity/slot-game/bin/model/GameModel";
import {ApiPlugin} from "@nolimitcity/slot-launcher/bin/plugins/ApiPlugin";
import {ArrayHelper} from "@nolimitcity/slot-game/bin/utils/ArrayHelper";
import {IBetWayWinData} from "@nolimitcity/slot-game/bin/utils/server/IData";
import {HOTBGameData, HOTBInitData, IBigWinLevel, IWinRatioConfig, IWinRatios, WinTypes} from "./HOTBData";
import {GameConfig} from "../GameConfig";
import {GameUtils} from "../utils/GameUtils";

export class ExtendedGameModel extends GameModel<HOTBInitData, HOTBGameData> {
    public nearWinReels: boolean[] = ArrayHelper.initArrayWithValues(GameConfig.totalReels, () => false);
    public isRestore: boolean = false;
    public isActionSpinToBonus: boolean = false;
    public reels: string[][];
    public winRatio: number;
    public updatedSymbolSizes: number[][] = [];
    public winRatioConfig: IWinRatioConfig;
    private WIN_RATIOS: IWinRatios = {
        normalWin: [
            {
                ratio: 0,
                duration: 0,
                type: WinTypes.TINY
            },
            {
                ratio: 1.5,
                duration: 0,
                type: WinTypes.SMALL
            },
            {
                ratio: 3,
                duration: 0,
                type: WinTypes.MEDIUM
            },
            {
                ratio: 5,
                duration: 0.75,
                type: WinTypes.LARGE
            }
        ],
        bigWin: [
            {
                from: 0,
                to: 25,
                id: 1,
                ratio: 15,
                duration: 3,
                type: WinTypes.BIG_WIN_01
            },
            {
                from: 25,
                to: 50,
                id: 2,
                ratio: 25,
                duration: 3.125,
                type: WinTypes.BIG_WIN_02
            },
            {
                from: 50,
                to: 100,
                id: 3,
                ratio: 50,
                duration: 5.250,
                type: WinTypes.BIG_WIN_03
            },
            {
                from: 100,
                to: 1000,
                id: 4,
                ratio: 100,
                duration: 5.250,
                type: WinTypes.BIG_WIN_04
            },
            {
                from: 1000,
                to: Number.MAX_VALUE,
                id: 5,
                ratio: 1000,
                duration: 10,
                type: WinTypes.BIG_WIN_05
            }
        ]
    };

    constructor(api: ApiPlugin, triggerEvents: boolean = true) {
        super(api, triggerEvents);
    }

    private _isPlayingBonusSpins: boolean = false;

    get isPlayingBonusSpins(): boolean {
        return this._isPlayingBonusSpins;
    }

    set isPlayingBonusSpins(value: boolean) {
        this._isPlayingBonusSpins = value;
    }

    private _evaluatedArea: string[][];

    get evaluatedArea(): string[][] {
        return this._evaluatedArea;
    }

    private _explodePositions: number[];

    get explodePositions() {
        return this._explodePositions;
    }

    private _allWinPositions: boolean[][] = [];

    get allWinPositions(): boolean[][] {
        return this._allWinPositions;
    }

    private _allExplodeWinPositions: boolean[][] = [];

    get allExplodeWinPositions(): boolean[][] {
        return this._allExplodeWinPositions;
    }

    public getBigWinLevels(ratio: number): IBigWinLevel[] {
        let levels: IBigWinLevel[] = [];
        const length: number = this.WIN_RATIOS.bigWin.length;
        for (let index: number = 0; index < length; index++) {
            if (ratio >= this.WIN_RATIOS.bigWin[index].ratio) {
                levels.push(JSON.parse(JSON.stringify(this.WIN_RATIOS.bigWin[index])));
            }
        }

        if (levels.length > 0) {
            levels = this.setLastConfig(ratio, levels);
        }
        return levels;
    }

    public onGame(data: HOTBGameData): void {
        super.onGame(data);
        this.isRestore = false;
        this.reels = data.reels;
        this._evaluatedArea = data.evaluatedArea;
        this._allExplodeWinPositions = this.calculateExplodeWinPositions(data.betWayWins);
        this.winRatio = (data.spinRoundWin / data.playedBetValue);
        this.winRatioConfig = this.calculateWinRatioConfig(this.winRatio);
    }

    public updateSymbolSizesAfterCollapse(): void {
        this.updatedSymbolSizes = JSON.parse(JSON.stringify(this.data.symbolSizes));

        for (let reelId: number = 0; reelId < this.data.deleteMap.length; reelId++) {
            for (let symId: number = 0; symId < this.data.deleteMap[reelId].length; symId++) {
                if (this.data.deleteMap[reelId][symId]) {
                    this.updatedSymbolSizes[reelId].splice(symId, 1);
                    this.updatedSymbolSizes[reelId].unshift(1);
                }
            }
        }
    }

    protected onInit(data: HOTBInitData): void {
        super.onInit(data);
        this.isRestore = data.isRestoreState;
    }

    private calculateWinRatioConfig(ratio: number): IWinRatioConfig {
        for (let index: number = this.WIN_RATIOS.normalWin.length - 1; index >= 0; index--) {
            if (ratio >= this.WIN_RATIOS.normalWin[index].ratio) {
                return this.WIN_RATIOS.normalWin[index];
            }
        }
        return this.WIN_RATIOS.normalWin[0];
    }

    private calculateExplodeWinPositions(winData: IBetWayWinData[]): boolean[][] {
        const allExplodeWinPos: boolean[][] = ArrayHelper.initArrayWithValues(GameConfig.totalReels, () => {
            return ArrayHelper.initArrayWithValues(GameConfig.totalSymsPerReel, () => false);
        });

        let reelSet: string[][] = this.evaluatedArea;

        winData.forEach((winGroup: IBetWayWinData): void => {
            let reelWinPositions: boolean[][] = ArrayHelper.initArrayWithValues(GameConfig.totalReels, () => {
                return ArrayHelper.initArrayWithValues(GameConfig.totalSymsPerReel, () => false);
            });

            for (let reelId: number = 0; reelId < reelSet.length; reelId++) {
                if (reelId < winGroup.length) {
                    for (let symId: number = 0; symId < reelSet[reelId].length; symId++) {
                        if (winGroup.symbol === reelSet[reelId][symId] || GameUtils.isWild(reelSet[reelId][symId])) {
                            allExplodeWinPos[reelId][symId] = true;
                            reelWinPositions[reelId][symId] = true;
                        }
                    }
                }
            }
            winGroup.reelWinPositions = reelWinPositions;
        });

        return allExplodeWinPos;
    }

    private setLastConfig(winRatio: number, configs: IBigWinLevel[]): IBigWinLevel[] {
        let lastConfig: IBigWinLevel = configs[configs.length - 1];
        if (lastConfig.from * 1.2 > winRatio && configs.length > 1) {
            configs.pop();
            return this.setLastConfig(winRatio, configs);
        }
        lastConfig.to = winRatio;
        return configs;
    }
}